<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">Alergias</h1>
        <p class="nd-page-kicker">Condições relevantes para segurança clínica e prescrições dos pacientes.</p>
      </div>
      <v-btn id="btnNew" type="button" color="primary" elevation="0" to="/alergias/novo">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid module="allergies" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Alergias</div>
          <div class="nd-table-toolbar__subtitle">Filtre, revise e atualize alertas clínicos.</div>
        </div>
        <v-form class="nd-table-toolbar__actions" @submit.prevent="load()">
          <v-text-field
            id="name"
            class="nd-search-field"
            dense
            placeholder="Buscar alergia"
            v-model="dataList.filter.name" hide-details="auto"
            outlined
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-text-field
            class="nd-search-field"
            dense
            placeholder="Buscar paciente"
            v-model="dataList.filter.patientName"
            hide-details="auto"
            outlined
          ></v-text-field>
          <v-btn id="btnFilter" type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="dataList.headers"
              :items="dataList.data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="dataList.count"
              :disable-sort="true"
              :loading="dataList.loading"
              no-data-text="Nenhuma alergia encontrada"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="dataList.options"
              :footer-props="dataList.footerOptions"
            >
              <template v-slot:[`item.name`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ item.name ? item.name.charAt(0) : "A" }}</span>
                  <div>
                    <strong>{{ item.name }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.medicines`]="{ item }">
                <span class="nd-chip-muted">{{ item.medicines && item.medicines.length ? item.medicines.join(", ") : "Sem medicamentos" }}</span>
              </template>

              <template v-slot:[`item.patient`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ item.patient && item.patient.name ? item.patient.name.charAt(0) : "P" }}</span>
                  <div>
                    <strong>{{ item.patient ? item.patient.name : "—" }}</strong>
                    <small>{{ item.patient ? `ID ${item.patient.id}` : "Sem paciente" }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.symptoms`]="{ item }">
                <span class="nd-chip-muted">{{ item.symptoms && item.symptoms.length ? item.symptoms.join(", ") : "Sem sintomas" }}</span>
              </template>

              <template v-slot:[`item.date`]="{ item }">
                {{ item.date ? new Date(item.date).toLocaleDateString('pt-BR') : '—' }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewItem(item)">
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Atualizar alergia</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deleteAllergy(item)">
                        <v-icon size="19">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover alergia</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import Vue from "vue";
import VueLodash from "vue-lodash";
import _ from "lodash";
import axios from "axios";

Vue.use(VueLodash, { lodash: _ });

export default {
  components: { ModuleStatGrid },
  name: "AllergiesList",

  data: () => ({
    dataset: {
      data: {},
    },
    dataList: {
      filter: {
        name: "",
        patientName: "",
      },
      headers: [
        { text: "Nome", value: "name" },
        { text: "Paciente", value: "patient" },
        { text: "Medicamentos", value: "medicines" },
        { text: "Sintomas", value: "symptoms" },
        { text: "Data", value: "date", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["id"],
        sortDesc: [false],
      },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
  }),

  created() {
    this.load();
  },

  computed: {
    lastRecordDate() {
      const latest = this.dataList.data.find((item) => item.date);
      return latest ? new Date(latest.date).toLocaleDateString("pt-BR") : "-";
    },
  },

  watch: {
    "dataList.data": {
      handler(newVal) {},
      deep: true,
      immediate: true,
    },
  },

  methods: {
    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.itemsPerPage = size;
      this.load(true);
    },

    async load() {
      const limit = this.dataList.options.itemsPerPage || 10;
      const params = {
        start: (this.dataList.options.page - 1) * limit,
        limit,
      };

      Object.entries(this.dataList.filter).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      this.dataList.loading = true;

      try {
        const result = await axios.get("/allergies", {
          params: params,
        });

        this.dataList.data = result.data.data;
        this.dataList.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error(
          "Ocorreu um erro ao encontrar as alergias, reinicie sua sessão e tente novamente!",
          {
            position: "top-right",
            timeout: 5000,
          }
        );
      } finally {
        this.dataList.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/alergias/${item.id}`);
    },

    async deleteAllergy(item) {
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover a alergia "${item.name}"?`,
        title: "Confirmar Exclusão",
        actions: {
          false: "Cancelar",
          true: { text: "Remover", color: "error" },
        },
      });

      if (proceed) {
        this.dataList.loading = true;
        try {
          await axios.delete(`/allergies/${item.id}`);
          this.$dialog.notify.info(`Alergia "${item.name}" removida com sucesso!`, {
            position: "top-right",
            timeout: 2000,
          });
          this.load();
        } catch (error) {
          console.error("Erro ao remover alergia:", error);
          this.$dialog.notify.error(
            "Ocorreu um erro ao remover a alergia. Tente novamente mais tarde.",
            {
              position: "top-right",
              timeout: 5000,
            }
          );
        } finally {
          this.dataList.loading = false;
        }
      }
    },
  },
};
</script>
